import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Add, 
  Cancel,
  VideoCall,
  Schedule,
  Assignment,
} from '@mui/icons-material';
import { appointmentAPI, Appointment, AppointmentStatus } from '../services/apiService';
import VideoConsultation from '../components/VideoConsultation';

const AppointmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    appointmentId: string;
    reason: string;
  }>({
    open: false,
    appointmentId: '',
    reason: '',
  });
  const [videoConsultation, setVideoConsultation] = useState<{
    open: boolean;
    appointment: Appointment | null;
  }>({
    open: false,
    appointment: null,
  });

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await appointmentAPI.getAppointments();
      // Sort appointments by date (upcoming first)
      const sortedAppointments = data.sort((a, b) => 
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      );
      setAppointments(sortedAppointments);
    } catch (err) {
      setError('Failed to load appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    try {
      await appointmentAPI.cancelAppointment(
        cancelDialog.appointmentId, 
        cancelDialog.reason || undefined
      );
      setCancelDialog({ open: false, appointmentId: '', reason: '' });
      loadAppointments(); // Refresh the list
    } catch (err) {
      setError('Failed to cancel appointment. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'success';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatAppointmentDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  const isUpcoming = (dateTime: string) => {
    return new Date(dateTime) > new Date();
  };

  const openVideoConsultation = (appointment: Appointment) => {
    setVideoConsultation({
      open: true,
      appointment,
    });
  };

  const closeVideoConsultation = () => {
    setVideoConsultation({
      open: false,
      appointment: null,
    });
  };

  const canJoinMeeting = (appointment: Appointment) => {
    if (appointment.status !== AppointmentStatus.BOOKED) return false;
    
    const appointmentTime = new Date(appointment.dateTime);
    const now = new Date();
    const timeDiffMinutes = (appointmentTime.getTime() - now.getTime()) / (1000 * 60);
    
    // Allow joining 15 minutes before appointment time and up to 2 hours after
    return timeDiffMinutes <= 15 && timeDiffMinutes >= -120;
  };

  const upcomingAppointments = appointments.filter(apt => 
    isUpcoming(apt.dateTime) && (apt.status === AppointmentStatus.BOOKED || apt.status === AppointmentStatus.IN_PROGRESS)
  );
  const pastAppointments = appointments.filter(apt => 
    !isUpcoming(apt.dateTime) || (apt.status === AppointmentStatus.COMPLETED || apt.status === AppointmentStatus.CANCELLED)
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <div>
          <Typography variant="h3" component="h1" gutterBottom>
            My Appointments
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage your FMLA consultation appointments
          </Typography>
        </div>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => navigate('/appointments/schedule')}
          sx={{ height: 'fit-content' }}
        >
          Schedule Appointment
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {appointments.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No appointments yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Schedule your first FMLA consultation with a licensed healthcare provider
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => navigate('/appointments/schedule')}
            >
              Schedule Your First Appointment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Upcoming Appointments */}
          {upcomingAppointments.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                Upcoming Appointments
              </Typography>
              {upcomingAppointments.map((appointment) => {
                const { date, time } = formatAppointmentDateTime(appointment.dateTime);
                return (
                  <Card key={appointment.appointmentId} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" gutterBottom>
                            {date}
                          </Typography>
                          <Typography variant="body1" color="primary" gutterBottom>
                            {time}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Provider: {appointment.providerEmail}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Appointment ID: {appointment.appointmentId.slice(0, 8)}...
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Chip 
                            label={appointment.status.toUpperCase()} 
                            color={getStatusColor(appointment.status) as any}
                            sx={{ mb: 1 }}
                          />
                          {appointment.notes && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Notes: {appointment.notes}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3} textAlign="right">
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                              variant="contained" 
                              startIcon={<VideoCall />}
                              size="small"
                              disabled={!canJoinMeeting(appointment)}
                              onClick={() => openVideoConsultation(appointment)}
                              sx={{ 
                                mb: 1,
                                backgroundColor: canJoinMeeting(appointment) ? 'success.main' : 'grey.400',
                                '&:hover': {
                                  backgroundColor: canJoinMeeting(appointment) ? 'success.dark' : 'grey.400',
                                },
                              }}
                            >
                              {canJoinMeeting(appointment) ? 'Join Call' : 'Meeting Not Ready'}
                            </Button>
                            <Button 
                              variant="outlined" 
                              color="primary"
                              startIcon={<Assignment />}
                              size="small"
                              onClick={() => navigate(`/assessments/portal/${appointment.appointmentId}`)}
                              sx={{ mb: 1 }}
                            >
                              Assessments
                            </Button>
                            <Button 
                              variant="outlined" 
                              color="error"
                              startIcon={<Cancel />}
                              size="small"
                              onClick={() => setCancelDialog({
                                open: true,
                                appointmentId: appointment.appointmentId,
                                reason: ''
                              })}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Grid>
          )}

          {/* Past Appointments */}
          {pastAppointments.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                Past Appointments
              </Typography>
              {pastAppointments.map((appointment) => {
                const { date, time } = formatAppointmentDateTime(appointment.dateTime);
                return (
                  <Card key={appointment.appointmentId} sx={{ mb: 2, opacity: 0.8 }}>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                          <Typography variant="h6" gutterBottom>
                            {date}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {time}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Provider: {appointment.providerEmail}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Appointment ID: {appointment.appointmentId.slice(0, 8)}...
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4} textAlign="right">
                          <Chip 
                            label={appointment.status.toUpperCase()} 
                            color={getStatusColor(appointment.status) as any}
                          />
                          {appointment.cancelledBy && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                              Cancelled by: {appointment.cancelledBy}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Grid>
          )}
        </Grid>
      )}

      {/* Cancel Appointment Dialog */}
      <Dialog 
        open={cancelDialog.open} 
        onClose={() => setCancelDialog({ open: false, appointmentId: '', reason: '' })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel this appointment?
          </Typography>
          <TextField
            label="Reason for cancellation (optional)"
            multiline
            rows={3}
            fullWidth
            value={cancelDialog.reason}
            onChange={(e) => setCancelDialog(prev => ({ ...prev, reason: e.target.value }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, appointmentId: '', reason: '' })}>
            Keep Appointment
          </Button>
          <Button 
            onClick={handleCancelAppointment}
            color="error"
            variant="contained"
          >
            Cancel Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Consultation Dialog */}
      {videoConsultation.appointment && (
        <VideoConsultation
          appointment={videoConsultation.appointment}
          open={videoConsultation.open}
          onClose={closeVideoConsultation}
          userType="patient"
        />
      )}
    </Container>
  );
};

export default AppointmentsPage;